import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { ReactElement, useCallback } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';

export interface NotesTextForm {
  title?: string;
  text?: string;
  type?: string;
}

const validationSchema = yup.object({
  title: yup.string().required('Il campo è obbligatorio'),
  text: yup.string().required('Il campo è obbligatorio'),
  type: yup.string().required('Il campo è obbligatorio'),
});

interface FormNotesTextProps {
  onOk: (form: NotesTextForm) => void;
  onCancel: (form?: NotesTextForm) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  isEdit?: boolean;
  initialValues?: NotesTextForm;
}

const FormNotesText = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
}: FormNotesTextProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formNotesText = useFormik<NotesTextForm>({
    initialValues: initialValues || {
      title: '',
      text: '',
      type: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formNotesText.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Titolo"
                  label="Titolo *"
                  id="title"
                  name="title"
                  type="text"
                  inputProps={{
                    maxLength: '50',
                  }}
                  variant="outlined"
                  value={formNotesText.values.title || ''}
                  disabled={disabled}
                  fullWidth
                  onChange={formNotesText.handleChange}
                  error={formNotesText.touched.title && Boolean(formNotesText.errors.title)}
                  helperText={formNotesText.errors.title}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Tipo"
                  label="Tipo *"
                  id="type"
                  name="type"
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '3',
                  }}
                  value={formNotesText.values.type || ''}
                  disabled={disabled}
                  fullWidth
                  onChange={formNotesText.handleChange}
                  error={formNotesText.touched.type && Boolean(formNotesText.errors.type)}
                  helperText={formNotesText.errors.type}
                />
              </Grid>
              <Grid item xs={12}>
                <PmiTextField
                  aria-label="Testo"
                  label="Testo *"
                  id="text"
                  name="text"
                  fullWidth
                  multiline
                  rows={5}
                  variant="outlined"
                  value={formNotesText.values.text || ''}
                  onChange={formNotesText.handleChange}
                  disabled={disabled}
                  error={formNotesText.touched.text && Boolean(formNotesText.errors.text)}
                  helperText={formNotesText.errors.text}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(formNotesText.values);
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormNotesText.displayName = 'Form Notes Text component';

export default FormNotesText;
